import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import {
  deleteAllThreads,
  deleteThread,
  getThreads,
  ThreadInteractionStatus,
  THREADS_PAGE_SIZE,
} from "helpers/api";
import { useMemo } from "react";

export const THREADS_QUERY_KEY = ["get_threads"];

// The interval at which we poll the threads API to check
// if any thread interactions are still in progress.
const POLLING_INTERVAL = 5000;

function useThreads(enabled?: boolean, searchTerm?: string) {
  const queryClient = useQueryClient();
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isError,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteQuery({
    queryKey: searchTerm
      ? [...THREADS_QUERY_KEY, searchTerm]
      : THREADS_QUERY_KEY,
    queryFn: async ({ pageParam = 0 }) => {
      const data = await getThreads(THREADS_PAGE_SIZE, pageParam, searchTerm);
      return data?.threads;
    },
    enabled: enabled,
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage?.length === THREADS_PAGE_SIZE)
        return allPages.length * THREADS_PAGE_SIZE;
      return undefined;
    },
    refetchInterval: (data) => {
      // Check if any threads have IN_PROGRESS status
      const hasInProgressThreads = data?.pages
        .flatMap((page) => page)
        .some(
          (thread) => thread?.status === ThreadInteractionStatus.IN_PROGRESS
        );

      return hasInProgressThreads ? POLLING_INTERVAL : false;
    },
    retryDelay(failureCount, error) {
      return failureCount ** 2 * 500; // 1st attempt: 500ms, 2nd: 2000ms, 3rd: 4500ms, 4th: 8000ms, etc.
    },
    retry(failureCount, error) {
      return failureCount < 10;
    },
    staleTime: 0,
  });

  const deleteThreadMutation = useMutation(
    async (threadId: string) => {
      return await deleteThread({ threadId });
    },
    {
      onSuccess: (_, threadId) => {
        // Optimistically remove the thread from the cache instead of invalidating
        queryClient.setQueryData(THREADS_QUERY_KEY, (oldData: any) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            pages: oldData.pages.map((page: any) =>
              page?.filter((thread: any) => thread.thread_id !== threadId)
            ),
          };
        });
        // Also update search-filtered queries if they exist
        queryClient.setQueriesData(
          { queryKey: THREADS_QUERY_KEY, exact: false },
          (oldData: any) => {
            if (!oldData) return oldData;
            return {
              ...oldData,
              pages: oldData.pages.map((page: any) =>
                page?.filter((thread: any) => thread.thread_id !== threadId)
              ),
            };
          }
        );
      },
    }
  );

  const deleteAllThreadsMutation = useMutation(
    async () => {
      return await deleteAllThreads({});
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(THREADS_QUERY_KEY);
      },
    }
  );

  // Concatenate all threads into a single array
  const threads = useMemo(
    () => data?.pages.flatMap((page) => page),
    [data?.pages]
  );

  return {
    fetchNextPage,
    hasNextPage,
    isError,
    isFetchingNextPage,
    isLoading: isLoading && enabled,
    threads,
    deleteThread: deleteThreadMutation.mutateAsync,
    deleteAllThreads: deleteAllThreadsMutation.mutateAsync,
  };
}

export default useThreads;
